import { useDeferredValue, useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiClientError, apiClient } from '../api';
import { useOnlineStatus, useVisitQueue } from '../hooks';
import { cachedLocations, isPendingVisit, queueVisit } from '../offline';
import type {
  Campaign,
  Location,
  Me,
  Paginated,
  PendingVisit,
  Run,
  RunParticipant,
  VisitActionType,
  VisitInput,
  Wave,
} from '../types';
import {
  EmptyState,
  ErrorNotice,
  Loading,
  ProgressBar,
  StatusPill,
  formString,
  relativeNzTime,
  stringValue,
  numberValue,
} from '../components/Common';

type WorkFilter = 'not_visited' | 'due' | 'completed' | 'inaccessible' | 'all';

function locationTone(location: Location): {
  tone: 'success' | 'warning' | 'danger' | 'neutral' | 'info';
  label: string;
} {
  if (
    ['restricted', 'inaccessible', 'do_not_distribute'].includes(location.permissionStatus) ||
    ['restricted', 'inaccessible'].includes(location.accessStatus)
  ) {
    return { tone: 'danger', label: 'Do not distribute' };
  }
  if (
    location.permissionStatus !== 'approved' ||
    location.distributionSuitability !== 'suitable' ||
    location.accessStatus !== 'accessible'
  ) {
    return { tone: 'warning', label: 'Approval needed' };
  }
  if (location.nextDueAt && Date.parse(location.nextDueAt) <= Date.now()) {
    return { tone: 'warning', label: 'Due for revisit' };
  }
  if (location.totalVisitCount > 0) return { tone: 'success', label: 'Completed' };
  return { tone: 'neutral', label: 'Not visited' };
}

function VisitSheet({
  location,
  campaign,
  wave,
  run,
  me,
  participants,
  onClose,
  onSaved,
}: {
  location: Location;
  campaign: Campaign;
  wave: Wave;
  run: Run | null;
  me: Me;
  participants: RunParticipant[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const online = useOnlineStatus();
  const [action, setAction] = useState<VisitActionType>(
    location.canRecordDrop ? 'drop' : 'permission_issue',
  );
  const defaultQuantity = location.targetQuantity ?? location.defaultTargetQuantity;
  const [quantity, setQuantity] = useState(defaultQuantity);
  const activeParticipants = participants.filter((participant) => !participant.left_at);
  const activeParticipantKey = JSON.stringify(
    activeParticipants.map((participant) => participant.id),
  );
  const [selectedParticipants, setSelectedParticipants] = useState(
    activeParticipants.length > 0
      ? activeParticipants.map((participant) => participant.id)
      : [me.user.id],
  );
  const [error, setError] = useState<unknown>(null);
  const firstButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const participantIds = JSON.parse(activeParticipantKey) as string[];
    setSelectedParticipants(participantIds.length > 0 ? participantIds : [me.user.id]);
  }, [activeParticipantKey, me.user.id, run?.id]);
  useEffect(() => {
    firstButton.current?.focus();
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  const save = useMutation({
    networkMode: 'always',
    mutationFn: async (event: VisitInput) => {
      if (!online) {
        const pending = await queueVisit(event, me.user.id);
        return { queued: true as const, warning: null as string | null, pending };
      }
      try {
        const response = await apiClient.recordVisit(event);
        return {
          queued: false as const,
          warning: response.data.reconciliationWarning,
          duplicate: response.data.possibleDuplicateOfEventId !== null,
        };
      } catch (caught) {
        if (
          !(caught instanceof ApiClientError) ||
          caught.status >= 500 ||
          caught.status === 429 ||
          caught.status === 401 ||
          (caught.status === 403 && caught.code === 'csrf_failed')
        ) {
          const pending = await queueVisit(event, me.user.id);
          return { queued: true as const, warning: null as string | null, pending };
        }
        throw caught;
      }
    },
    onSuccess: async (result, event) => {
      if (result.queued) {
        queryClient.setQueryData<PendingVisit[]>(['visit-queue'], (records = []) => [
          ...records.filter((record) => record.idempotencyKey !== result.pending.idempotencyKey),
          result.pending,
        ]);
        queryClient.setQueriesData<Paginated<Location>>({ queryKey: ['locations'] }, (page) =>
          page
            ? {
                ...page,
                data: page.data.map((item) =>
                  item.id === event.locationId
                    ? {
                        ...item,
                        lastVisitAt: event.clientOccurredAt,
                        totalVisitCount: item.totalVisitCount + 1,
                        totalFlyersDistributed: item.totalFlyersDistributed + event.flyerQuantity,
                      }
                    : item,
                ),
              }
            : page,
        );
      } else {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['visit-queue'] }),
          queryClient.invalidateQueries({ queryKey: ['locations'] }),
          queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
          queryClient.invalidateQueries({ queryKey: ['runs'] }),
          queryClient.invalidateQueries({ queryKey: ['inventory'] }),
        ]);
      }
      const message = result.queued
        ? 'Saved offline — this visit is pending sync.'
        : result.duplicate
          ? 'Saved with a possible duplicate warning.'
          : result.warning
            ? `Saved. ${result.warning}`
            : 'Visit saved.';
      onSaved(message);
    },
    onError: setError,
  });

  const changeAction = (next: VisitActionType) => {
    setAction(next);
    if (next !== 'drop') setQuantity(0);
    else setQuantity(defaultQuantity);
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const form = new FormData(event.currentTarget);
    const outcome =
      action === 'drop' || action === 'inspection_no_drop'
        ? 'accessed'
        : action === 'inaccessible'
          ? 'inaccessible'
          : 'approval_needed';
    const note = formString(form, 'notes').trim();
    save.mutate({
      id: crypto.randomUUID(),
      idempotencyKey: crypto.randomUUID(),
      campaignId: campaign.id,
      locationId: location.id,
      waveId: wave.id,
      runId: run?.id ?? null,
      clientOccurredAt: new Date().toISOString(),
      flyerQuantity: action === 'drop' ? quantity : 0,
      actionType: action,
      ...(note ? { notes: note } : {}),
      accessOutcome: outcome,
      participantUserIds: selectedParticipants,
      source: online ? 'online' : 'offline_sync',
      clientAppVersion: '1.0.0',
    });
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="bottom-sheet visit-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="visit-sheet-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sheet-handle" aria-hidden="true" />
        <div className="sheet-heading">
          <div>
            <p className="eyebrow">
              {location.campus.name} · {location.building.name}
            </p>
            <h2 id="visit-sheet-title">{location.roomCode}</h2>
            <p>
              {location.locationType}
              {location.capacity ? ` · ${location.capacity} seats` : ''}
            </p>
          </div>
          <button
            ref={firstButton}
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="last-visit-context">
          <strong>{relativeNzTime(location.lastVisitAt)}</strong>
          <span>
            {location.totalVisitCount} visit{location.totalVisitCount === 1 ? '' : 's'} ·{' '}
            {location.totalFlyersDistributed} flyers total
          </span>
        </div>
        {!location.canRecordDrop ? (
          <div className="notice notice-safety" role="alert">
            <strong>Drop blocked</strong>
            <span>
              Do not enter locked, restricted, staff-only, or otherwise unauthorized spaces. Record
              what happened below.
            </span>
          </div>
        ) : null}
        {error ? <ErrorNotice error={error} title="Could not save visit" /> : null}
        <form className="form-stack" onSubmit={submit}>
          <fieldset>
            <legend>Visit outcome</legend>
            <div className="outcome-grid">
              <button
                type="button"
                className={action === 'drop' ? 'selected' : ''}
                disabled={!location.canRecordDrop}
                onClick={() => changeAction('drop')}
              >
                <span aria-hidden="true">＋</span>
                Record drop
              </button>
              <button
                type="button"
                className={action === 'inspection_no_drop' ? 'selected' : ''}
                onClick={() => changeAction('inspection_no_drop')}
              >
                <span aria-hidden="true">∅</span>
                No flyers left
              </button>
              <button
                type="button"
                className={action === 'inaccessible' ? 'selected' : ''}
                onClick={() => changeAction('inaccessible')}
              >
                <span aria-hidden="true">×</span>
                Could not access
              </button>
              <button
                type="button"
                className={action === 'permission_issue' ? 'selected' : ''}
                onClick={() => changeAction('permission_issue')}
              >
                <span aria-hidden="true">!</span>
                Approval needed
              </button>
            </div>
          </fieldset>
          {action === 'drop' ? (
            <fieldset>
              <legend>Flyers left</legend>
              <div className="quantity-presets">
                {[1, 2, 5, 10].map((value) => (
                  <button
                    type="button"
                    className={quantity === value ? 'selected' : ''}
                    onClick={() => setQuantity(value)}
                    key={value}
                  >
                    {value}
                  </button>
                ))}
                <label>
                  <span>Custom</span>
                  <input
                    type="number"
                    min={1}
                    max={10_000}
                    value={quantity}
                    onChange={(event) => setQuantity(Number(event.target.value))}
                    aria-label="Custom flyer quantity"
                    required
                  />
                </label>
              </div>
            </fieldset>
          ) : null}
          {run ? (
            <fieldset>
              <legend>Who was at this room?</legend>
              <p className="field-help">Select the whole group, a split group, or only yourself.</p>
              <div className="participant-grid">
                {activeParticipants.map((participant) => (
                  <label key={participant.id}>
                    <input
                      type="checkbox"
                      checked={selectedParticipants.includes(participant.id)}
                      onChange={(event) => {
                        setSelectedParticipants((current) =>
                          event.target.checked
                            ? [...new Set([...current, participant.id])]
                            : current.filter((id) => id !== participant.id),
                        );
                      }}
                      disabled={participant.id === me.user.id}
                    />
                    {participant.display_name}
                    {participant.id === me.user.id ? ' (you)' : ''}
                  </label>
                ))}
              </div>
            </fieldset>
          ) : (
            <p className="solo-note">This visit will be attributed to you alone.</p>
          )}
          <label>
            Note <span className="optional">(optional)</span>
            <textarea name="notes" rows={2} maxLength={2000} />
          </label>
          <button
            className="button button-primary button-full button-save"
            disabled={
              save.isPending ||
              (action === 'drop' && quantity <= 0) ||
              selectedParticipants.length === 0
            }
          >
            {save.isPending ? 'Saving…' : online ? 'Save visit' : 'Save offline'}
          </button>
        </form>
      </section>
    </div>
  );
}

export function WorkPage({
  campaign,
  me,
  waveId,
  runId,
  onWave,
  onRun,
}: {
  campaign: Campaign;
  me: Me;
  waveId: string | null;
  runId: string | null;
  onWave: (id: string) => void;
  onRun: (id: string | null) => void;
}) {
  const [campusId, setCampusId] = useState<string | null>(null);
  const [buildingId, setBuildingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<WorkFilter>('not_visited');
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [selected, setSelected] = useState<Location | null>(null);
  const [toast, setToast] = useState('');
  const queue = useVisitQueue();
  const waves = useQuery({
    queryKey: ['waves', campaign.id],
    queryFn: () => apiClient.waves(campaign.id),
  });
  const runs = useQuery({
    queryKey: ['runs', campaign.id],
    queryFn: () => apiClient.runs(campaign.id),
  });
  const inventory = useQuery({
    queryKey: ['inventory', campaign.id],
    queryFn: () => apiClient.inventoryAccounts(campaign.id),
  });
  const dashboard = useQuery({
    queryKey: ['dashboard', campaign.id],
    queryFn: () => apiClient.dashboard(campaign.id),
  });
  const selectedWave =
    waves.data?.data.find((wave) => wave.id === waveId) ?? waves.data?.data[0] ?? null;
  const selectedRun = runs.data?.data.find((run) => run.id === runId) ?? null;
  const focusedRunId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!waveId && selectedWave) onWave(selectedWave.id);
  }, [onWave, selectedWave, waveId]);
  const campuses = dashboard.data?.data.campuses ?? [];
  const buildings = (dashboard.data?.data.buildings ?? []).filter(
    (building) => !campusId || stringValue(building.campus_id) === campusId,
  );
  useEffect(() => {
    const matching = campuses.some((campus) => stringValue(campus.id) === campusId);
    const nextCampus = matching ? campusId : stringValue(campuses[0]?.id) || null;
    if (nextCampus !== campusId) setCampusId(nextCampus);
  }, [campusId, campuses]);
  useEffect(() => {
    const matching = buildings.some((building) => stringValue(building.id) === buildingId);
    const nextBuilding = matching ? buildingId : stringValue(buildings[0]?.id) || null;
    if (nextBuilding !== buildingId) setBuildingId(nextBuilding);
  }, [buildingId, buildings]);
  useEffect(() => {
    const nextRunId = selectedRun?.id ?? null;
    if (focusedRunId.current === nextRunId) return;
    focusedRunId.current = nextRunId;
    if (selectedRun?.campus?.id) {
      setCampusId(selectedRun.campus.id);
      setBuildingId(selectedRun.building?.id ?? null);
    } else if (selectedRun?.building?.id) {
      setBuildingId(selectedRun.building.id);
    }
  }, [selectedRun?.building?.id, selectedRun?.campus?.id, selectedRun?.id]);

  const locationsKey = JSON.stringify({
    campaignId: campaign.id,
    waveId: selectedWave?.id,
    campusId,
    buildingId,
    filter,
    search: deferredSearch,
  });
  const locations = useQuery({
    queryKey: ['locations', locationsKey],
    enabled: Boolean(selectedWave && buildingId),
    queryFn: () =>
      cachedLocations(locationsKey, me.user.id, () =>
        apiClient.allLocations({
          campaignId: campaign.id,
          waveId: selectedWave?.id,
          campusId,
          buildingId,
          status: filter,
          search: deferredSearch,
        }),
      ),
  });
  const participants = useQuery({
    queryKey: ['run-participants', selectedRun?.id],
    enabled: Boolean(selectedRun),
    queryFn: () => apiClient.participants(selectedRun!.id),
  });

  const pending = queue.data ?? [];
  const pendingDropQuantity = pending
    .filter(
      (record) =>
        (record.event.runId ?? null) === (selectedRun?.id ?? null) &&
        isPendingVisit(record) &&
        record.event.actionType === 'drop',
    )
    .reduce((total, record) => total + record.event.flyerQuantity, 0);
  const ownStock =
    inventory.data?.data.find(
      (account) => account.accountType === 'volunteer' && account.userId === me.user.id,
    )?.balance ?? 0;
  const buildingProgress = dashboard.data?.data.buildings.find(
    (building) => stringValue(building.id) === buildingId,
  );
  const currentLocations = locations.data?.data ?? [];
  const openLocation = (location: Location) => setSelected(location);
  const saved = (message: string) => {
    const currentIndex = currentLocations.findIndex((location) => location.id === selected?.id);
    const next = currentLocations[currentIndex + 1];
    setSelected(null);
    setToast(next ? `${message} Next: ${next.roomCode}.` : message);
    window.setTimeout(() => setToast(''), 5_000);
    if (next) {
      window.setTimeout(
        () =>
          document.getElementById(`location-${next.id}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          }),
        150,
      );
    }
  };

  if (waves.isLoading || dashboard.isLoading || inventory.isLoading) {
    return <Loading label="Preparing route…" />;
  }
  if (waves.error || dashboard.error || inventory.error) {
    return <ErrorNotice error={waves.error ?? dashboard.error ?? inventory.error} />;
  }
  if (!selectedWave) {
    return (
      <EmptyState title="No distribution wave">
        Ask a coordinator to create a distribution wave before recording work.
      </EmptyState>
    );
  }

  return (
    <div className="page work-page">
      {toast ? (
        <div className="toast" role="status">
          {toast}
        </div>
      ) : null}
      <header className="page-heading">
        <div>
          <p className="eyebrow">Today’s work</p>
          <h1>Room route</h1>
        </div>
        <StatusPill tone={selectedRun ? 'info' : 'neutral'}>
          {selectedRun ? selectedRun.status : 'Solo'}
        </StatusPill>
      </header>
      <div className="route-summary">
        <label>
          Wave
          <select value={selectedWave.id} onChange={(event) => onWave(event.target.value)}>
            {waves.data?.data.map((wave) => (
              <option key={wave.id} value={wave.id}>
                {wave.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Run or solo
          <select
            value={selectedRun?.id ?? ''}
            onChange={(event) => onRun(event.target.value || null)}
          >
            <option value="">Working alone</option>
            {runs.data?.data
              .filter((run) => ['planned', 'active', 'paused'].includes(run.status))
              .map((run) => (
                <option value={run.id} key={run.id}>
                  {run.name} · {run.status}
                </option>
              ))}
          </select>
        </label>
        <div className="stock-card">
          <span>{selectedRun ? 'Run stock remaining' : 'Your stock'}</span>
          <strong>
            {Math.max(
              0,
              (selectedRun ? numberValue(selectedRun.stockBalance) : ownStock) -
                pendingDropQuantity,
            )}
          </strong>
          {pendingDropQuantity > 0 ? <small>{pendingDropQuantity} pending</small> : null}
        </div>
        <div className="group-card">
          <span>Working with</span>
          <strong>
            {selectedRun
              ? selectedRun.participants.join(', ') || `${selectedRun.participantCount} people`
              : me.user.displayName}
          </strong>
        </div>
      </div>
      <div className="notice notice-safety">
        <strong>Permission comes first</strong>
        <span>
          Never enter locked, restricted, staff-only, or unauthorized spaces. Use “Could not access”
          or “Approval needed” instead.
        </span>
      </div>
      <section className="route-controls" aria-label="Route filters">
        <div className="two-column-fields">
          <label>
            Campus
            <select
              value={campusId ?? ''}
              onChange={(event) => {
                setCampusId(event.target.value);
                setBuildingId(null);
              }}
            >
              {campuses.map((campus) => (
                <option value={stringValue(campus.id)} key={stringValue(campus.id)}>
                  {stringValue(campus.name)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Building
            <select
              value={buildingId ?? ''}
              onChange={(event) => setBuildingId(event.target.value)}
            >
              {buildings.map((building) => (
                <option value={stringValue(building.id)} key={stringValue(building.id)}>
                  {stringValue(building.name)}
                </option>
              ))}
            </select>
          </label>
        </div>
        {buildingProgress ? (
          <ProgressBar
            value={numberValue(buildingProgress.visited)}
            max={numberValue(buildingProgress.total)}
            label={`${stringValue(buildingProgress.name)} progress`}
          />
        ) : null}
        <label className="search-field">
          <span className="sr-only">Search rooms or buildings</span>
          <input
            type="search"
            placeholder="Search room code or building"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <div className="filter-chips" role="group" aria-label="Location status">
          {(
            [
              ['not_visited', 'Not visited'],
              ['due', 'Due'],
              ['completed', 'Completed'],
              ['inaccessible', 'Inaccessible'],
              ['all', 'All'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={filter === value ? 'selected' : ''}
              onClick={() => setFilter(value)}
              aria-pressed={filter === value}
            >
              {label}
            </button>
          ))}
        </div>
      </section>
      {locations.isLoading ? <Loading label="Loading rooms…" /> : null}
      {locations.error ? <ErrorNotice error={locations.error} /> : null}
      {!locations.isLoading && currentLocations.length === 0 ? (
        <EmptyState title="No rooms match">
          Change the building, status filter, or search.
        </EmptyState>
      ) : (
        <section className="location-list" aria-label="Rooms">
          {currentLocations.map((location) => {
            const status = locationTone(location);
            const pendingForLocation = pending.filter(
              (record) => record.event.locationId === location.id && isPendingVisit(record),
            ).length;
            return (
              <button
                className="location-card"
                id={`location-${location.id}`}
                type="button"
                key={location.id}
                onClick={() => openLocation(location)}
              >
                <span className="room-code">{location.roomCode}</span>
                <span className="location-main">
                  <strong>{location.locationType}</strong>
                  <span>
                    {location.capacity ? `${location.capacity} seats · ` : ''}
                    {relativeNzTime(location.lastVisitAt)}
                  </span>
                  <span>
                    {location.nextDueAt
                      ? `Next due ${new Intl.DateTimeFormat('en-NZ', {
                          timeZone: 'Pacific/Auckland',
                          dateStyle: 'medium',
                        }).format(new Date(location.nextDueAt))}`
                      : 'No revisit due'}
                  </span>
                </span>
                <span className="location-status">
                  {pendingForLocation > 0 ? (
                    <StatusPill tone="info">{pendingForLocation} pending</StatusPill>
                  ) : (
                    <StatusPill tone={status.tone}>{status.label}</StatusPill>
                  )}
                  <span aria-hidden="true">›</span>
                </span>
              </button>
            );
          })}
        </section>
      )}
      {selected ? (
        <VisitSheet
          location={selected}
          campaign={campaign}
          wave={selectedWave}
          run={selectedRun}
          me={me}
          participants={participants.data?.data ?? []}
          onClose={() => setSelected(null)}
          onSaved={saved}
        />
      ) : null}
    </div>
  );
}
